import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// Storage keys for tracking banner state
const BANNER_STORAGE_KEY = 'promotional_banner_state';
const MAX_SHOWS = 3;
const MAX_CLOSES = 3;
const MAX_CLICKS = 3;

interface BannerState {
  showCount: number;
  closeCount: number;
  clickCount: number;
  lastShown: string;
}

// Utility function to reset banner state (for testing/development)
// Can be called from browser console: resetPromotionalBanner()
(window as any).resetPromotionalBanner = () => {
  localStorage.removeItem(BANNER_STORAGE_KEY);
  console.log('[BANNER] Banner state reset - will show again on next page load');
  window.location.reload();
};

export const PromotionalBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);

  const messages = [
    "Follow us and join our WhatsApp channel for latest updates and winner announcements!",
    "Refer a friend and get 35 BONUS entries each - double your chances to win!",
    "Join our exciting competitions today and win amazing prizes!"
  ];

  const AUTO_HIDE_DURATION = 3 * 60 * 1000; // 3 minutes in milliseconds
  const FADE_OUT_DURATION = 1000; // 1 second fade out

  // Initialize banner visibility based on stored state
  useEffect(() => {
    const checkBannerVisibility = () => {
      try {
        const storedState = localStorage.getItem(BANNER_STORAGE_KEY);
        const bannerState: BannerState = storedState 
          ? JSON.parse(storedState)
          : { showCount: 0, closeCount: 0, clickCount: 0, lastShown: '' };

        // Don't show if user has closed it 3 times
        if (bannerState.closeCount >= MAX_CLOSES) {
          console.log('[BANNER] Not showing: User has closed banner too many times');
          setIsVisible(false);
          return;
        }

        // Don't show if user has clicked it 3 times
        if (bannerState.clickCount >= MAX_CLICKS) {
          console.log('[BANNER] Not showing: User has clicked banner too many times');
          setIsVisible(false);
          return;
        }

        // Don't show if already shown maximum times
        if (bannerState.showCount >= MAX_SHOWS) {
          console.log('[BANNER] Not showing: Already shown maximum times');
          setIsVisible(false);
          return;
        }

        // Don't show if already shown today
        const today = new Date().toDateString();
        if (bannerState.lastShown === today) {
          console.log('[BANNER] Not showing: Already shown today');
          setIsVisible(false);
          return;
        }

        // Show the banner and increment show count
        setIsVisible(true);
        const updatedState: BannerState = {
          ...bannerState,
          showCount: bannerState.showCount + 1,
          lastShown: today
        };
        localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(updatedState));
        console.log(`[BANNER] Showing banner (${updatedState.showCount}/${MAX_SHOWS})`);

      } catch (error) {
        console.error('[BANNER] Error checking banner visibility:', error);
        setIsVisible(true); // Default to showing if error
      }
    };

    checkBannerVisibility();
  }, []);

  // Handle message cycling
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => 
        (prevIndex + 1) % messages.length
      );
    }, 4000); // Change message every 4 seconds

    return () => clearInterval(interval);
  }, [isVisible, messages.length]);

  // Auto-hide after 3 minutes
  useEffect(() => {
    if (!isVisible) return;

    console.log('[BANNER] Auto-hide timer started - banner will disappear in 3 minutes');

    // Start fade out after 3 minutes
    const fadeOutTimer = setTimeout(() => {
      console.log('[BANNER] Starting fade out animation');
      setOpacity(0);
      
      // Completely hide after fade animation completes
      const hideTimer = setTimeout(() => {
        console.log('[BANNER] Auto-hiding banner after 3 minutes');
        setIsVisible(false);
      }, FADE_OUT_DURATION);

      return () => clearTimeout(hideTimer);
    }, AUTO_HIDE_DURATION);

    return () => clearTimeout(fadeOutTimer);
  }, [isVisible]);

  // Handle banner close
  const handleClose = () => {
    try {
      const storedState = localStorage.getItem(BANNER_STORAGE_KEY);
      const bannerState: BannerState = storedState 
        ? JSON.parse(storedState)
        : { showCount: 0, closeCount: 0, clickCount: 0, lastShown: '' };

      const updatedState: BannerState = {
        ...bannerState,
        closeCount: bannerState.closeCount + 1
      };

      localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(updatedState));
      console.log(`[BANNER] Banner closed (${updatedState.closeCount}/${MAX_CLOSES})`);
      
      if (updatedState.closeCount >= MAX_CLOSES) {
        console.log('[BANNER] Banner will not show again - closed too many times');
      }
    } catch (error) {
      console.error('[BANNER] Error updating close count:', error);
    }
    
    setIsVisible(false);
  };

  // Handle banner content click
  const handleBannerClick = () => {
    try {
      const storedState = localStorage.getItem(BANNER_STORAGE_KEY);
      const bannerState: BannerState = storedState 
        ? JSON.parse(storedState)
        : { showCount: 0, closeCount: 0, clickCount: 0, lastShown: '' };

      const updatedState: BannerState = {
        ...bannerState,
        clickCount: bannerState.clickCount + 1
      };

      localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(updatedState));
      console.log(`[BANNER] Banner clicked (${updatedState.clickCount}/${MAX_CLICKS})`);
      
      if (updatedState.clickCount >= MAX_CLICKS) {
        console.log('[BANNER] Banner will not show again - clicked too many times');
        setIsVisible(false);
      }
    } catch (error) {
      console.error('[BANNER] Error updating click count:', error);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="text-black py-3 px-4 relative shadow-lg cursor-pointer transition-opacity duration-1000 ease-in-out" 
      style={{ backgroundColor: '#FCCE56', opacity }} 
      onClick={handleBannerClick}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Banner Content */}
        <div className="flex-1 text-center">
          <p className="text-lg font-bold transition-opacity duration-500">
            {messages[currentMessageIndex]}
          </p>
          <div className="flex justify-center gap-4 mt-2">
            <a 
              href="https://www.facebook.com/people/Prize-Drop/61578226929455/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-700 hover:text-blue-900 font-semibold underline"
              data-testid="link-facebook"
            >
              Facebook
            </a>
            <span className="text-black">•</span>
            <a 
              href="https://whatsapp.com/channel/0029VbBr6DbJ3juzjTPAlo3b" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-900 font-semibold underline"
              data-testid="link-whatsapp"
            >
              WhatsApp Channel
            </a>
          </div>
        </div>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering banner click
            handleClose();
          }}
          className="absolute top-2 right-2 text-black hover:bg-black/10 p-1"
          data-testid="button-close-banner"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};